import React from 'react';
import { Stage } from '@/types/journey';
import { EmotionLevel } from './EmotionSelector';
import { getEmotionPercentage } from '@/utils/emotionHelpers';

interface EmotionCurveProps {
  stages: Stage[];
  fixedHeight?: number; // New prop for fixed-height stage cards
}

const EmotionCurve: React.FC<EmotionCurveProps> = ({ stages, fixedHeight }) => {
  // Early return if less than 2 stages
  if (stages.length < 2) return null;
  
  // Define a fixed height for the emotion curve container
  // This helps ensure consistent positioning regardless of stage content heights
  const curveContainerHeight = 170; // Further reduced height for tighter face alignment
  
  // Convert emotion levels to vertical positions - now using fixed percentages
  const getEmotionY = (emotion: number): number => {
    // Get emotion percentage (0-100) where 0 is top (wow) and 100 is bottom (frustrated)
    const percentage = getEmotionPercentage(emotion as EmotionLevel);
    
    // Map directly to position in container with a small offset to center on face
    // The +6 offset helps align the curve with the center of the emotion faces
    return (percentage / 100) * curveContainerHeight + 6;
  };
  
  // Calculate points for the curve
  const points = stages.map((stage, index) => {
    // Each stage is now 336px wide (20% wider than previous 280px) with 24px gap
    const stageWidth = 336; // Width of the card - updated to match the 20% wider cards
    const stageGap = 24; // Gap between cards
    
    // Calculate the horizontal position where the face icon is located
    const totalWidth = stageWidth + stageGap;
    
    // The horizontal offset is adjusted to match the position of emotion faces
    // in the grid layout of the JourneyStage component footer for wider cards
    const faceIconCenterX = (index * totalWidth) + 270; // Adjusted to center on faces (previously 235)
    
    // Calculate Y position using our fixed scale
    const y = getEmotionY(stage.emotion);
    
    return { x: faceIconCenterX, y };
  });
  
  // Generate SVG path from points
  const generateSvgPath = (points: { x: number; y: number }[]): string => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Create a smooth curve through all points
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      
      // Calculate control points for smooth curve
      const controlX = (current.x + next.x) / 2;
      
      path += ` C ${controlX} ${current.y}, ${controlX} ${next.y}, ${next.x} ${next.y}`;
    }
    
    return path;
  };
  
  const svgPath = generateSvgPath(points);
  
  // Calculate SVG dimensions to ensure it covers all stages
  const stageWidth = 336; // Updated to match the 20% wider cards
  const stageGap = 24;
  const width = (stages.length - 1) * (stageWidth + stageGap) + stageWidth + 100; // Add extra padding
  
  // Position the curve to align with the emotion faces in the card footers
  // Based on the image, we need to adjust the vertical position
  const footerOffset = 100; // Distance from bottom of card to center of emotion faces
  const topOffset = fixedHeight 
    ? fixedHeight - footerOffset - 90 // Adjusted upward by 60px
    : 80; // Default position when no fixed height
    
  return (
    <div 
      className="absolute left-0 z-10 pointer-events-none" 
      style={{ 
        overflow: 'visible',
        top: `${topOffset}px`, // Position at the emotion faces level
        height: `${curveContainerHeight}px` // Fixed height container
      }}
    >
      <svg 
        width={width} 
        height={curveContainerHeight}
        className="overflow-visible"
        style={{ 
          position: 'absolute',
          left: '0',
          top: '0',
        }}
      >
        {/* Reference lines for the 5 emotion levels - uncomment for debugging */}
        {/* <line x1="0" y1={getEmotionY(2)} x2={width} y2={getEmotionY(2)} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
        <line x1="0" y1={getEmotionY(1)} x2={width} y2={getEmotionY(1)} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
        <line x1="0" y1={getEmotionY(0)} x2={width} y2={getEmotionY(0)} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
        <line x1="0" y1={getEmotionY(-1)} x2={width} y2={getEmotionY(-1)} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" />
        <line x1="0" y1={getEmotionY(-2)} x2={width} y2={getEmotionY(-2)} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="2,2" /> */}
        
        <path
          d={svgPath}
          fill="none"
          stroke="#C8C8C9"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="5,5"
          className="journey-connection"
        />
      </svg>
    </div>
  );
};

export default EmotionCurve;
